import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { Key, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { RedoList, RedoListItem } from "./arbiter-components/list/redo-list";
import { RedoListItemSize } from "./arbiter-components/list/standard-redo-list-items";
import * as expandableDropdownCss from "./expandable-dropdown.module.css";
import { Flex } from "./flex";
import ChevronDown from "./icon-old/chevron-down.svg";

interface ExpandableDropdownProps<T> {
  header: ReactNode;
  id?: string;
  items: RedoListItem<T>[];
  className?: string;
  value: T[];
  valueChange(value: T[]): void;
  overrideOpen?: boolean;
  onOpen?(id: Key): void;
  size?: RedoListItemSize;
}

export const ExpandableDropdown = genericMemo(function ExpandableDropdown<T>({
  header,
  id,
  items,
  overrideOpen,
  onOpen,
  size = "md",
  value,
  valueChange,
}: ExpandableDropdownProps<T>) {
  const [open, setOpen] = useState(overrideOpen || false);

  useEffect(() => {
    if (overrideOpen !== undefined) {
      setOpen(overrideOpen);
    }
  }, [overrideOpen]);

  useEffect(() => {
    if (open && onOpen && id !== undefined) {
      onOpen(id);
    }
  }, [id, open, onOpen]);

  const ref = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
    undefined,
  );
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (measureRef.current) {
      const height = measureRef.current.scrollHeight;
      setContentHeight(height);
    }
  }, [items.length, size]);

  const selectedIds = useMemo(() => {
    return value
      .map((v) => {
        const item = items.find((i) => i.value === v);
        return item?.id;
      })
      .filter((id): id is string | number | symbol => id !== undefined);
  }, [value, items]);

  const handleItemSelected = useHandler(
    (args: {
      item: RedoListItem<T>;
      source: string;
      index: number;
      selected: boolean;
    }) => {
      const itemValue = args.item.value;
      const isSelected = value.includes(itemValue);
      if (isSelected) {
        valueChange(value.filter((v) => v !== itemValue));
      } else {
        valueChange([...value, itemValue]);
      }
    },
  );

  const list = (
    <RedoList
      focusedIndex={focusedIndex}
      items={items}
      itemSelected={handleItemSelected}
      refToListenTo={ref.current}
      selectedItems={selectedIds}
      selectionVariant="checkbox-reverse"
      setFocusedIndex={setFocusedIndex}
      size={size}
    />
  );

  return (
    <Flex dir="column">
      {/* Hidden measurement container */}
      <div
        aria-hidden="true"
        ref={measureRef}
        style={{ height: 0, overflow: "hidden", pointerEvents: "none" }}
      >
        {list}
      </div>

      <Flex
        className={expandableDropdownCss.item}
        justify="space-between"
        onClick={() => setOpen(!open)}
      >
        <Flex>{header}</Flex>
        <Flex ref={ref}>
          <ChevronDown
            className={expandableDropdownCss.chevron}
            height={20}
            style={{ transform: `rotate(${open ? "180deg" : "0deg"})` }}
            width={20}
          />
        </Flex>
      </Flex>
      <Flex
        className={expandableDropdownCss.dropdownItems}
        dir="column"
        overflow="hidden"
        style={{ height: open ? `${contentHeight}px` : "0px" }}
      >
        {list}
      </Flex>
    </Flex>
  );
});
